import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@fragments";
import { IconArrowLeft } from "@tabler/icons-react";

import useEnrollmentDetail from "./hooks/useEnrollmentDetail";
import EnrollmentDetailView from "./view";
import EnrollmentActionModal from "../components/EnrollmentActionModal";

/**
 * Enrollment detail — §6.4 (itemId = enrollmentId). Detail panel with the
 * creator/community sections, program terms, the split-window timeline, the
 * split_only callout and the Insights snapshot. The inline review/end actions
 * (§6.7–6.8) flow through the shared EnrollmentActionModal and refresh on done.
 */
export default function MentoringEnrollmentDetails({ setPageActions }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug, subtab: programId, itemId: enrollmentId } = useParams();
  const state = useEnrollmentDetail(programId, enrollmentId);
  const actionRef = useRef({});

  const { enrollment, currentCommunity, refresh } = state;
  const communityId = currentCommunity?.id;

  const onAction = useCallback(
    (action) => {
      if (!enrollment) return;
      actionRef.current.open?.({ enrollment, action, onDone: refresh });
    },
    [enrollment, refresh],
  );

  // The list carries its active tab here as ?tab= (see the enrollments view);
  // restore it on the way back so we land on the tab the user came from.
  const tab = searchParams.get("tab");

  useEffect(() => {
    if (!setPageActions) return;
    // Always return to the program's enrollments list (with the originating
    // tab). Browser back is unreliable here: after "Visualizar aplicação"
    // round-trips through the form answers page (return_to pushes back here),
    // navigate(-1) would land on that answers page instead of the list.
    const goBack = () =>
      navigate(`/${slug}/settings/mentoring/${programId}${tab ? `?tab=${tab}` : ""}`);

    setPageActions(
      <Button
        text="Voltar às inscrições"
        startIcon={<IconArrowLeft />}
        color="overlay"
        size="small"
        onClick={goBack}
      />
    );
    return () => setPageActions(null);
  }, [setPageActions, slug, programId, navigate, tab]);

  return (
    <>
      <EnrollmentDetailView {...state} onAction={onAction} />
      <EnrollmentActionModal modalRef={actionRef} communityId={communityId} />
    </>
  );
}
